import { CommonModule, DatePipe } from '@angular/common';
import {
    AfterViewChecked,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    ViewChild,
    computed,
    inject,
    signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { Drawer } from 'primeng/drawer';
import { Dialog } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { ToastrService } from 'ngx-toastr';
import { Subject, interval, switchMap, takeUntil, finalize, catchError, of, tap } from 'rxjs';
import { TablerIconsModule } from 'angular-tabler-icons';
import {
    IChatwootContact,
    IChatwootConversationContext,
    IChatwootMessage,
    IChatwootMessageTemplate,
    IChatwootParametrosIaRequest,
    IChatwootTemplateComponent,
    ISendTemplateMessageRequest
} from '../../interfaces/IChatwoot';
import { ChatwootService } from '../../services/chatwoot.service';
import { ChatwootNotificationService } from '../../services/chatwoot-notification.service';
import { TagModule } from 'primeng/tag';
import { MultiSelectModule } from 'primeng/multiselect';
import { TooltipModule } from 'primeng/tooltip';

const POLL_INTERVAL_MS = 10_000;
const PLACEHOLDER_REGEX = /{{\s*(\d+)\s*}}/g;

/** Límite del body de PLANTILLAS de WhatsApp (Meta Cloud API); espejo de
 *  WhatsAppTemplateHelper.MaxLongitudBodyPlantilla en el backend y de campana-form. */
const LIMITE_BODY_PLANTILLA = 1024;

/**
 * Espejo cliente de WhatsAppTemplateHelper.EsParamInvalido (backend) y de campana-form:
 * la Cloud API de WhatsApp rechaza (#132018) VALORES de parámetro {{N}} que traigan salto
 * de línea, retorno de carro, tab o 5+ espacios consecutivos. Los textos largos/con formato
 * van en el body de la plantilla aprobada, no acá.
 */
function esParamInvalido(valor: string | null | undefined): boolean {
    if (!valor) return false;
    return /[\n\r\t]/.test(valor) || / {5,}/.test(valor);
}

@Component({
    selector: 'app-chat-candidato-drawer',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        Drawer,
        Dialog,
        ButtonModule,
        InputTextModule,
        TextareaModule,
        ProgressSpinnerModule,
        TablerIconsModule,
        TagModule,
        MultiSelectModule,
        TooltipModule,
        DatePipe
    ],
    templateUrl: './chat-candidato-drawer.component.html',
    styleUrls: ['./chat-candidato-drawer.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatCandidatoDrawerComponent implements OnDestroy, AfterViewChecked {
    private readonly chatwootService = inject(ChatwootService);
    private readonly notificationService = inject(ChatwootNotificationService);
    private readonly toastr = inject(ToastrService);
    private readonly destroy$ = new Subject<void>();
    private readonly stopPolling$ = new Subject<void>();

    @Input() idCandidato: number | null = null;
    @Input() phone: string | null = null;
    @Input() nombre: string | null = null;

    /** true cuando el chat se abrió desde el panel del FAB: muestra "← volver al listado". */
    @Input() desdePanelFab = false;

    @Input() visible = false;
    @Output() visibleChange = new EventEmitter<boolean>();

    readonly loading = signal(false);
    readonly mensajesLoading = signal(false);
    readonly enviando = signal(false);
    readonly mensajes = signal<IChatwootMessage[]>([]);
    readonly conversationId = signal<number | null>(null);
    /** created_at (epoch s) de la conversación activa — para etiquetar mensajes del polling
     *  con conversation_created_at y que el sort del histórico multi-conversación no los
     *  mande al inicio (ca=0). */
    private activeConvCreatedAt: number | null = null;
    readonly canReply = signal<boolean>(true);
    readonly errorMessage = signal<string | null>(null);

    /** Reloj reactivo: se refresca en cada tick del polling (10 s) para que `ventanaAbierta`
     *  se reevalúe y la ventana pueda expirar con el chat abierto. */
    readonly ahora = signal<number>(Date.now());

    /**
     * Ventana de 24 h de WhatsApp (regla de Meta): solo se puede enviar TEXTO LIBRE si existe
     * algún mensaje ENTRANTE (message_type === 0) dentro de las últimas 24 h. Se calcula sobre
     * los mensajes ya cargados (fuente objetiva) porque can_reply de Chatwoot puede venir
     * optimista en conversaciones nuevas/reutilizadas. created_at viene en epoch SEGUNDOS.
     */
    readonly ventanaAbierta = computed<boolean>(() => {
        const limiteSegundos = Math.floor(this.ahora() / 1000) - 24 * 60 * 60;
        return this.mensajes().some((m) => m.message_type === 0 && m.created_at >= limiteSegundos);
    });

    /** Gate compuesto para texto libre/adjuntos: Chatwoot debe permitir responder (can_reply)
     *  Y la ventana de 24 h debe estar abierta. Las plantillas NO pasan por este gate. */
    readonly puedeEscribir = computed<boolean>(() => this.canReply() && this.ventanaAbierta());

    // Adjuntos pendientes (antes de enviar)
    readonly pendingFiles = signal<{ id: number; file: File }[]>([]);
    private pendingFileSeq = 0;
    private static readonly MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024; // 100 MB

    // Plantillas
    readonly templatesVisible = signal(false);
    readonly templates = signal<IChatwootMessageTemplate[]>([]);
    readonly templatesLoading = signal(false);
    readonly selectedTemplate = signal<IChatwootMessageTemplate | null>(null);
    readonly templateParams = signal<Record<string, string>>({});

    // Completar parámetros con IA (mismo endpoint/payload que campana-form)
    readonly iaDialogVisible = signal(false);
    readonly iaTextoLibre = signal('');
    readonly iaCompletando = signal(false);

    // Contexto (labels, custom attributes, contact)
    readonly infoExpanded = signal(false);
    readonly contextLoading = signal(false);
    readonly context = signal<IChatwootConversationContext | null>(null);
    readonly updatingAttribute = signal<string | null>(null);
    readonly availableLabels = signal<{ title: string; color?: string }[]>([]);

    readonly labels = computed(() => this.context()?.labels ?? []);
    readonly conversationAttrs = computed(() => this.toAttributeEntries(this.context()?.conversationCustomAttributes));
    readonly contactAttrs = computed(() => this.toAttributeEntries(this.context()?.contact?.custom_attributes));
    readonly contactInfo = computed(() => this.context()?.contact ?? null);
    readonly botEnabled = computed<boolean>(() => {
        const attrs = this.context()?.conversationCustomAttributes;
        // Default ON cuando el atributo no existe: el bot solo deja de responder si está explícitamente OFF
        if (!attrs || !('estado_bot' in attrs)) return true;
        return this.parseBoolean(attrs['estado_bot']);
    });

    nuevoMensaje = '';
    private shouldScrollToBottom = false;

    /** Límite de cuerpo de texto de WhatsApp. Más largo = no se entrega (se bloquea el envío). */
    readonly LIMITE_CARACTERES = 4096;

    get excedeLimite(): boolean {
        return this.nuevoMensaje.length > this.LIMITE_CARACTERES;
    }

    /**
     * Indicador de entrega para un mensaje SALIENTE (normal type=1 o plantilla type=3).
     * - failed   → no entregado (rojo + label visible "No entregado" + motivo).
     * - read     → leído (azul).
     * - delivered→ entregado (✓).
     * - sent     → enviado, sin confirmación de entrega (gris).
     * - sin status → "sin confirmación" (neutro, gris): Chatwoot no reporta; NO afirmar entregado ni fallido.
     * Devuelve null solo para entrantes / actividad. El polling refresca el status.
     */
    estadoEntrega(m: IChatwootMessage): { cls: string; color: string; tooltip: string; label?: string } | null {
        const esSaliente = m.message_type === 1 || m.message_type === 3;
        if (!esSaliente) return null;

        switch (m.status) {
            case 'failed': {
                const err = m.content_attributes?.external_error;
                return { cls: 'pi pi-times-circle', color: 'estado-fallido', tooltip: 'No entregado' + (err ? `: ${err}` : ''), label: 'No entregado' };
            }
            case 'read':
                return { cls: 'pi pi-check-circle', color: 'estado-leido', tooltip: 'Leído' };
            case 'delivered':
                return { cls: 'pi pi-check-circle', color: 'estado-entregado', tooltip: 'Entregado' };
            case 'sent':
                return { cls: 'pi pi-check', color: 'estado-enviado', tooltip: 'Enviado (sin confirmación de entrega aún)' };
            default:
                // Sin status (o uno desconocido): Chatwoot no confirma entrega. Neutro, honesto.
                return { cls: 'pi pi-clock', color: 'estado-pendiente', tooltip: 'Sin confirmación de entrega' };
        }
    }

    @ViewChild('messagesContainer') messagesContainer?: ElementRef<HTMLDivElement>;

    readonly templatePlaceholders = computed<string[]>(() => {
        const tpl = this.selectedTemplate();
        if (!tpl) return [];
        const body = this.getBodyComponent(tpl);
        if (!body?.text) return [];
        const set = new Set<string>();
        let match: RegExpExecArray | null;
        const re = new RegExp(PLACEHOLDER_REGEX);
        while ((match = re.exec(body.text)) !== null) {
            set.add(match[1]);
        }
        return Array.from(set).sort((a, b) => Number(a) - Number(b));
    });

    readonly templatePreview = computed<string>(() => {
        const tpl = this.selectedTemplate();
        if (!tpl) return '';
        const body = this.getBodyComponent(tpl);
        if (!body?.text) return '';
        const params = this.templateParams();
        return body.text.replace(PLACEHOLDER_REGEX, (_, n) => params[n] ?? `{{${n}}}`);
    });

    /** Límite del body renderizado de una plantilla (Meta). Expuesto para el template. */
    readonly LIMITE_BODY_PLANTILLA = LIMITE_BODY_PLANTILLA;

    /**
     * Placeholders cuyo valor viola la regla #132018 de Meta (salto de línea/tab/5+ espacios).
     * Los inputs del dialog son de una línea, pero un pegado puede colar tabs o espacios
     * múltiples. Espejo de campana-form.parametrosInvalidos — bloquea "Enviar".
     */
    readonly paramsInvalidos = computed<Set<string>>(() => {
        const params = this.templateParams();
        const invalidos = new Set<string>();
        for (const p of this.templatePlaceholders()) {
            if (esParamInvalido(params[p])) invalidos.add(p);
        }
        return invalidos;
    });

    /** True si el body renderizado (preview) supera el límite de 1024 de plantillas — bloquea "Enviar". */
    readonly previewExcede = computed<boolean>(() => this.templatePreview().length > LIMITE_BODY_PLANTILLA);

    /** Habilita "✨ Completar con IA": la plantilla seleccionada debe tener placeholders. */
    readonly puedeUsarIa = computed<boolean>(() => this.templatePlaceholders().length > 0);

    onShow(): void {
        if (!this.phone) {
            this.toastr.error(
                this.idCandidato ? 'Candidato sin celular registrado.' : 'Contacto sin número de teléfono.',
                'Chat'
            );
            this.close();
            return;
        }

        this.reset();
        this.loading.set(true);

        // Con idCandidato → by-candidate (flujo original de candidatos, sin cambios).
        // Sin idCandidato (chat global / campañas) → by-phone; la respuesta tiene el mismo shape.
        const open$ = this.idCandidato
            ? this.chatwootService.openConversation({
                  idCandidato: this.idCandidato,
                  phone: this.phone,
                  nombre: this.nombre ?? undefined
              })
            : this.chatwootService.openConversationByPhone(this.phone, this.nombre ?? undefined);

        open$
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.loading.set(false))
            )
            .subscribe({
                next: (resp) => {
                    if (!resp?.success || !resp?.data) {
                        this.errorMessage.set(resp?.message ?? 'No se pudo abrir la conversación.');
                        return;
                    }
                    this.conversationId.set(resp.data.id);
                    this.activeConvCreatedAt = resp.data.created_at ?? null;
                    this.canReply.set(resp.data.can_reply ?? true);
                    // Carga histórico completo del contacto (todas las conversaciones del inbox)
                    this.fetchAllMessagesByPhone();
                    this.fetchContext();
                    this.startPolling();
                    // Abrir el chat = leerlo: sincroniza unread_count en Chatwoot (campanita/FAB/bandeja).
                    this.markSeenSilently(resp.data.id);

                    // Registrar la conversación para notificaciones globales
                    if (this.idCandidato && this.phone) {
                        this.notificationService.register({
                            conversationId: resp.data.id,
                            idCandidato: this.idCandidato,
                            phone: this.phone,
                            nombre: this.nombre ?? this.phone
                        });
                        this.notificationService.markRead(this.idCandidato);
                    }
                },
                error: (err) => {
                    const status = err?.status ?? 0;
                    const detail = err?.error?.message ?? err?.message ?? 'sin detalle';
                    this.errorMessage.set(`Error al abrir conversación (status ${status}): ${detail}`);
                }
            });
    }

    onHide(): void {
        this.stopPolling$.next();
        this.visibleChange.emit(false);
        this.reset();
    }

    onVisibleChange(value: boolean): void {
        this.visible = value;
        this.visibleChange.emit(value);
        if (!value) {
            this.stopPolling$.next();
        }
    }

    enviar(): void {
        const convId = this.conversationId();
        const content = this.nuevoMensaje.trim();
        const files = this.pendingFiles().map((p) => p.file);
        if (!convId || this.enviando()) return;
        if (!content && files.length === 0) return;

        // Guard de ventana de 24 h (la API también bloquea): texto libre solo si el contacto
        // escribió en las últimas 24 h; de lo contrario Meta rechaza el envío async.
        if (!this.puedeEscribir()) {
            this.toastr.warning(
                'Ventana de 24 h cerrada: el contacto debe escribirte primero. Puedes enviar una plantilla aprobada.',
                'Chat'
            );
            return;
        }

        // Guard de longitud (la API también bloquea): un mensaje sobre el límite de WhatsApp
        // se acepta pero NO se entrega. Mejor avisar acá antes de enviar.
        if (content.length > this.LIMITE_CARACTERES) {
            this.toastr.warning(
                `El mensaje tiene ${content.length} caracteres y supera el límite de ${this.LIMITE_CARACTERES} de WhatsApp. Acortalo o divídelo.`,
                'Mensaje muy largo'
            );
            return;
        }

        // Si hay adjuntos, ruta con multipart
        if (files.length > 0) {
            this.enviarConAdjuntos(convId, content, files);
            return;
        }

        this.enviando.set(true);
        const optimisticId = -Date.now();
        const optimistic: IChatwootMessage = {
            id: optimisticId,
            content,
            message_type: 1,
            created_at: Math.floor(Date.now() / 1000)
        };
        this.mensajes.update((arr) => [...arr, optimistic]);
        this.nuevoMensaje = '';
        this.shouldScrollToBottom = true;

        this.chatwootService
            .sendMessage(convId, content)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.enviando.set(false))
            )
            .subscribe({
                next: (resp) => {
                    if (resp?.success && resp?.data) {
                        this.mensajes.update((arr) => arr.map((m) => (m.id === optimisticId ? resp.data : m)));
                    } else {
                        this.mensajes.update((arr) => arr.filter((m) => m.id !== optimisticId));
                        this.toastr.error(resp?.message ?? 'No se envió el mensaje.', 'Chat');
                    }
                },
                error: () => {
                    this.mensajes.update((arr) => arr.filter((m) => m.id !== optimisticId));
                    this.toastr.error('Error de red al enviar.', 'Chat');
                }
            });
    }

    private enviarConAdjuntos(convId: number, content: string, files: File[]): void {
        this.enviando.set(true);
        const optimisticId = -Date.now();
        const optimistic: IChatwootMessage = {
            id: optimisticId,
            content: content || '',
            message_type: 1,
            created_at: Math.floor(Date.now() / 1000),
            attachments: files.map((f, i) => ({
                id: optimisticId - i,
                file_type: this.guessFileType(f.type),
                data_url: '', // sin URL aún (optimistic)
                extension: f.name.split('.').pop(),
                file_size: f.size
            }))
        };
        this.mensajes.update((arr) => [...arr, optimistic]);
        this.nuevoMensaje = '';
        this.pendingFiles.set([]);
        this.shouldScrollToBottom = true;

        this.chatwootService
            .sendMessageWithAttachments(convId, content || null, files)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.enviando.set(false))
            )
            .subscribe({
                next: (resp) => {
                    if (resp?.success && resp?.data) {
                        this.mensajes.update((arr) => arr.map((m) => (m.id === optimisticId ? resp.data : m)));
                    } else {
                        this.mensajes.update((arr) => arr.filter((m) => m.id !== optimisticId));
                        this.toastr.error(resp?.message ?? 'No se enviaron los adjuntos.', 'Chat');
                    }
                },
                error: (err) => {
                    this.mensajes.update((arr) => arr.filter((m) => m.id !== optimisticId));
                    const msg = err?.error?.message ?? err?.message ?? 'Error al enviar adjuntos.';
                    this.toastr.error(msg, 'Chat');
                }
            });
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;
        if (file.size > ChatCandidatoDrawerComponent.MAX_FILE_SIZE_BYTES) {
            this.toastr.error(`El archivo excede 100 MB.`, 'Adjunto');
            input.value = '';
            return;
        }
        this.pendingFiles.update((arr) => [...arr, { id: ++this.pendingFileSeq, file }]);
        input.value = ''; // permitir reseleccionar el mismo archivo
    }

    removePendingFile(id: number): void {
        this.pendingFiles.update((arr) => arr.filter((p) => p.id !== id));
    }

    proxyUrl(dataUrl: string | undefined): string {
        if (!dataUrl) return '';
        return this.chatwootService.getAttachmentProxyUrl(dataUrl);
    }

    /**
     * Descarga el adjunto: fetch como blob → crea objectURL → anchor con download attr.
     * Garantiza diálogo "Guardar" del navegador sin abrir pestaña ni reproducir inline.
     */
    async downloadAttachment(att: { data_url?: string; extension?: string }, event?: Event): Promise<void> {
        event?.preventDefault();
        event?.stopPropagation();
        if (!att?.data_url) return;

        try {
            const url = this.proxyUrl(att.data_url);
            // Endpoint /attachments/proxy es anónimo; sin credentials evita problemas CORS cross-origin localhost
            const resp = await fetch(url);
            if (!resp.ok) throw new Error(`status ${resp.status}`);
            const blob = await resp.blob();
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = this.attachmentName(att);
            document.body.appendChild(a);
            a.click();
            a.remove();
            // Liberar memoria del objectURL
            setTimeout(() => URL.revokeObjectURL(objectUrl), 5000);
        } catch (err) {
            this.toastr.error('No se pudo descargar el adjunto.', 'Chat');
        }
    }

    attachmentName(att: { data_url?: string; extension?: string }): string {
        // Extensión real provista por Chatwoot (ej. "pdf"); normalizada sin punto.
        const ext = (att.extension ?? '').toLowerCase().replace(/^\.+/, '').trim();

        // Nombre base = último segmento del path del data_url (puede traer basura al final,
        // ej. "...pdf-filename_=" porque Chatwoot guardó el blob con un nombre mal formado).
        let base = '';
        if (att.data_url) {
            try {
                const url = new URL(att.data_url);
                base = decodeURIComponent(url.pathname.split('/').filter(Boolean).pop() ?? '');
            } catch { /* data_url inválida → se usa el fallback de abajo */ }
        }

        if (ext) {
            // Corta TODO lo que venga después de la extensión real (la basura) y la re-agrega limpia.
            const i = base.toLowerCase().lastIndexOf('.' + ext);
            if (i >= 0) base = base.slice(0, i);
            base = base.replace(/[\\/:*?"<>|]+/g, '_').replace(/\.+$/, '').trim();
            return `${base || 'archivo'}.${ext}`;
        }

        // Sin extensión conocida: detectar una extensión "sana" embebida (ej. ".pdf" seguido de
        // basura) y cortar todo lo posterior; si no hay, devolver el nombre saneado tal cual.
        const m = base.match(/\.([a-z0-9]{1,5})(?=[^a-z0-9.]|$)/i);
        if (m && m.index !== undefined) {
            base = base.slice(0, m.index + m[0].length);
        }
        base = base.replace(/[\\/:*?"<>|]+/g, '_').trim();
        return base || 'archivo';
    }

    formatFileSize(bytes: number | undefined | null): string {
        if (!bytes || bytes <= 0) return '';
        const units = ['B', 'KB', 'MB', 'GB'];
        let i = 0;
        let size = bytes;
        while (size >= 1024 && i < units.length - 1) {
            size /= 1024;
            i++;
        }
        return `${size.toFixed(size >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
    }

    private guessFileType(mime: string): string {
        if (mime.startsWith('image/')) return 'image';
        if (mime.startsWith('audio/')) return 'audio';
        if (mime.startsWith('video/')) return 'video';
        return 'file';
    }

    onEnterPress(event: KeyboardEvent): void {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.enviar();
        }
    }

    openTemplatesDialog(): void {
        const convId = this.conversationId();
        if (!convId) return;

        this.selectedTemplate.set(null);
        this.templateParams.set({});
        this.resetIaState();
        this.templatesVisible.set(true);

        if (this.templates().length > 0) return;

        this.templatesLoading.set(true);
        this.chatwootService
            .getTemplates(convId)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.templatesLoading.set(false))
            )
            .subscribe({
                next: (resp) => {
                    if (resp?.success && resp?.data) {
                        this.templates.set(resp.data);
                    } else {
                        this.toastr.error(resp?.message ?? 'No se pudieron cargar plantillas.', 'Plantillas');
                    }
                },
                error: () => this.toastr.error('Error al cargar plantillas.', 'Plantillas')
            });
    }

    selectTemplate(tpl: IChatwootMessageTemplate, event?: Event): void {
        event?.stopPropagation();
        event?.preventDefault();
        // eslint-disable-next-line no-console
        console.log('[Chatwoot] selectTemplate called:', tpl?.name);
        this.selectedTemplate.set(tpl);
        const placeholders = this.templatePlaceholders();
        const initial: Record<string, string> = {};
        placeholders.forEach((p) => (initial[p] = ''));
        this.templateParams.set(initial);
        this.resetIaState();
    }

    backToTemplateList(): void {
        this.selectedTemplate.set(null);
        this.templateParams.set({});
        this.resetIaState();
    }

    closeTemplatesDialog(): void {
        this.templatesVisible.set(false);
    }

    onTemplatesVisibleChange(value: boolean): void {
        this.templatesVisible.set(value);
        if (!value) {
            this.selectedTemplate.set(null);
            this.templateParams.set({});
            this.resetIaState();
        }
    }

    onParamChange(key: string, value: string): void {
        this.templateParams.update((p) => ({ ...p, [key]: value }));
    }

    // ─── Completar parámetros con IA ─────────────────────────────────────────

    abrirDialogoIa(): void {
        if (!this.puedeUsarIa()) return;
        this.iaTextoLibre.set('');
        this.iaDialogVisible.set(true);
    }

    /**
     * Envía el texto libre del agente al endpoint de IA (mismo endpoint/payload que
     * campana-form) y aplica los valores devueltos a los parámetros de la plantilla
     * seleccionada: pisa los que vengan con valor no vacío, no toca los que la IA deje
     * vacíos (lo ya escrito a mano se conserva). Las validaciones existentes (#132018,
     * límite 1024) siguen corriendo sobre lo que quede — la IA no las esquiva.
     */
    completarConIa(): void {
        if (this.iaCompletando()) return;
        const texto = this.iaTextoLibre().trim();
        if (!texto) {
            this.toastr.warning('Pega el texto del mensaje que quieres enviar.', 'Completar con IA');
            return;
        }
        const tpl = this.selectedTemplate();
        if (!tpl) return;

        const dto: IChatwootParametrosIaRequest = {
            textoLibre: texto,
            templateName: tpl.name ?? '',
            templateLanguage: tpl.language ?? 'es',
            bodyPlantilla: this.getBodyComponent(tpl)?.text ?? '',
            placeholders: this.templatePlaceholders().map((p) => ({
                placeholder: p,
                // Label real del admin (o null): el fallback visual "Parámetro" no le sirve a la IA.
                label: tpl.placeholder_labels?.find((l) => l.placeholder === p)?.label ?? null
            }))
        };

        this.iaCompletando.set(true);
        this.chatwootService
            .completarParametrosIa(dto)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.iaCompletando.set(false))
            )
            .subscribe({
                next: (res) => {
                    if (!res.success) {
                        this.toastr.error(res.message || 'No fue posible completar los parámetros con IA.', 'Completar con IA');
                        return;
                    }
                    const valores = res.data ?? {};
                    let llenados = 0;
                    this.templateParams.update((params) => {
                        const nuevo = { ...params };
                        for (const p of this.templatePlaceholders()) {
                            const valor = valores[p];
                            if (!valor) continue;
                            nuevo[p] = valor;
                            llenados++;
                        }
                        return nuevo;
                    });
                    if (llenados > 0) {
                        this.toastr.success(`La IA completó ${llenados} parámetro(s), revísalos antes de enviar.`, 'Completar con IA');
                        this.iaDialogVisible.set(false);
                    } else {
                        this.toastr.info(
                            'La IA no encontró valores para los parámetros en el texto. Ajusta el texto o llénalos manualmente.',
                            'Completar con IA'
                        );
                    }
                },
                error: (err) => {
                    this.toastr.error(
                        err.error?.mensaje ?? err.error?.detalle ?? 'No fue posible completar los parámetros con IA.',
                        'Completar con IA'
                    );
                }
            });
    }

    private resetIaState(): void {
        this.iaDialogVisible.set(false);
        this.iaTextoLibre.set('');
        this.iaCompletando.set(false);
    }

    enviarPlantilla(): void {
        const convId = this.conversationId();
        const tpl = this.selectedTemplate();
        if (!convId || !tpl?.name || this.enviando()) return;

        const params = this.templateParams();
        const placeholders = this.templatePlaceholders();
        const missing = placeholders.filter((p) => !params[p]?.trim());
        if (missing.length > 0) {
            this.toastr.warning(`Completa los parámetros: ${missing.map((m) => `{{${m}}}`).join(', ')}`, 'Plantilla');
            return;
        }

        // Guard #132018 (la API también bloquea): parámetros con salto de línea/tab/espacios
        // múltiples hacen que Meta rechace la plantilla completa.
        const invalidos = Array.from(this.paramsInvalidos());
        if (invalidos.length > 0) {
            this.toastr.warning(
                `Parámetro(s) ${invalidos.map((p) => `{{${p}}}`).join(', ')}: sin saltos de línea ni tabs, ` +
                    'WhatsApp rechaza parámetros multilínea (#132018).',
                'Plantilla'
            );
            return;
        }

        // Guard de longitud del body renderizado (la API también bloquea): Meta rechaza
        // plantillas cuyo cuerpo final supere 1024 caracteres.
        const renderedLen = this.templatePreview().length;
        if (renderedLen > LIMITE_BODY_PLANTILLA) {
            this.toastr.warning(
                `El mensaje renderizado tiene ${renderedLen} caracteres y supera el límite de ` +
                    `${LIMITE_BODY_PLANTILLA} de WhatsApp para plantillas. Acorta los valores de los parámetros.`,
                'Plantilla'
            );
            return;
        }

        const payload: ISendTemplateMessageRequest = {
            templateName: tpl.name,
            category: tpl.category ?? 'UTILITY',
            language: tpl.language ?? 'es',
            params
        };

        this.enviando.set(true);
        const optimisticId = -Date.now();
        const optimistic: IChatwootMessage = {
            id: optimisticId,
            content: this.templatePreview(),
            message_type: 1,
            created_at: Math.floor(Date.now() / 1000)
        };
        this.mensajes.update((arr) => [...arr, optimistic]);
        this.shouldScrollToBottom = true;

        this.chatwootService
            .sendTemplate(convId, payload)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.enviando.set(false))
            )
            .subscribe({
                next: (resp) => {
                    if (resp?.success && resp?.data) {
                        this.mensajes.update((arr) => arr.map((m) => (m.id === optimisticId ? resp.data : m)));
                        this.canReply.set(true);
                        this.templatesVisible.set(false);
                        this.selectedTemplate.set(null);
                        this.toastr.success('Plantilla enviada.', 'Chat');
                    } else {
                        this.mensajes.update((arr) => arr.filter((m) => m.id !== optimisticId));
                        this.toastr.error(resp?.message ?? 'No se envió la plantilla.', 'Plantilla');
                    }
                },
                error: (err) => {
                    this.mensajes.update((arr) => arr.filter((m) => m.id !== optimisticId));
                    const detail = err?.error?.message ?? 'Error de red.';
                    this.toastr.error(detail, 'Plantilla');
                }
            });
    }

    close(): void {
        this.visible = false;
        this.visibleChange.emit(false);
        this.stopPolling$.next();
    }

    /** "← Volver al listado": cierra el chat y reabre el panel del FAB (lista hidratada del snapshot). */
    volverAlListado(): void {
        this.stopPolling$.next();
        this.notificationService.volverAlPanelFab();
    }

    nombreCompleto(): string {
        return this.nombre?.trim() || this.phone || 'Candidato';
    }

    isOutgoing(m: IChatwootMessage): boolean {
        return m.message_type === 1 || m.message_type === 3;
    }

    isActivity(m: IChatwootMessage): boolean {
        return m.message_type === 2;
    }

    senderLabel(m: IChatwootMessage): string {
        if (this.isOutgoing(m)) {
            return m.sender?.name ?? 'Agente';
        }
        return m.sender?.name ?? this.contactInfo()?.name ?? this.nombre ?? 'Contacto';
    }

    async copyMessage(m: IChatwootMessage): Promise<void> {
        const text = m.content ?? '';
        if (!text) return;
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                const ta = document.createElement('textarea');
                ta.value = text;
                ta.style.position = 'fixed';
                ta.style.opacity = '0';
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
            }
            this.toastr.success('Mensaje copiado.', 'Chat', { timeOut: 1500 });
        } catch {
            this.toastr.error('No se pudo copiar.', 'Chat');
        }
    }

    trackById(_: number, m: IChatwootMessage): number {
        return m.id;
    }

    /**
     * Devuelve true si este mensaje arranca una nueva conversación
     * comparado con el anterior en la lista. Usa para dibujar separadores.
     */
    isNewConversation(index: number, m: IChatwootMessage): boolean {
        if (!m.conversation_id) return false;
        if (index === 0) return true;
        const prev = this.mensajes()[index - 1];
        return prev?.conversation_id !== m.conversation_id;
    }

    /** Etiqueta humana para el separador de conversación. */
    conversationSeparatorLabel(m: IChatwootMessage): string {
        const convId = m.conversation_id;
        const status = m.conversation_status ? ` · ${m.conversation_status}` : '';
        const dateStr = m.conversation_created_at
            ? new Date(m.conversation_created_at * 1000).toLocaleDateString('es-CO', {
                  year: 'numeric',
                  month: 'short',
                  day: '2-digit'
              })
            : '';
        const dateTag = dateStr ? ` · ${dateStr}` : '';
        const isActive = convId === this.conversationId();
        const activeTag = isActive ? '  (actual)' : '';
        return `Conversación #${convId}${status}${dateTag}${activeTag}`;
    }

    trackTemplate(_: number, t: IChatwootMessageTemplate): string {
        return t.name ?? '';
    }

    getBodyComponent(tpl: IChatwootMessageTemplate): IChatwootTemplateComponent | undefined {
        return tpl.components?.find((c) => (c.type ?? '').toUpperCase() === 'BODY');
    }

    /** Etiqueta humana del placeholder (definida por admin) o fallback "Parámetro {{N}}". */
    placeholderLabel(placeholder: string): string {
        const tpl = this.selectedTemplate();
        const label = tpl?.placeholder_labels?.find((l) => l.placeholder === placeholder)?.label;
        return label && label.trim().length > 0 ? label : `Parámetro`;
    }

    ngAfterViewChecked(): void {
        if (this.shouldScrollToBottom && this.messagesContainer) {
            this.scrollToBottom();
            this.shouldScrollToBottom = false;
        }
    }

    private scrollToBottom(): void {
        const tryScroll = (attempt: number): void => {
            const el = this.messagesContainer?.nativeElement;
            if (!el) {
                if (attempt < 10) requestAnimationFrame(() => tryScroll(attempt + 1));
                return;
            }
            el.scrollTop = el.scrollHeight;
            // Algunos elementos tardan un frame extra en medir altura final.
            if (attempt < 3) requestAnimationFrame(() => tryScroll(attempt + 1));
        };
        requestAnimationFrame(() => tryScroll(0));
    }

    ngOnDestroy(): void {
        this.stopPolling$.next();
        this.stopPolling$.complete();
        this.destroy$.next();
        this.destroy$.complete();
    }

    private startPolling(): void {
        const convId = this.conversationId();
        if (!convId) return;

        interval(POLL_INTERVAL_MS)
            .pipe(
                takeUntil(this.stopPolling$),
                takeUntil(this.destroy$),
                // Refresca el reloj reactivo en cada tick: la ventana de 24 h puede expirar
                // con el chat abierto y `ventanaAbierta` debe reevaluarse.
                tap(() => this.ahora.set(Date.now())),
                switchMap(() => this.chatwootService.getMessages(convId))
            )
            .subscribe({
                next: (resp) => {
                    if (resp?.success && resp?.data) {
                        this.mergeMessages(resp.data);
                    }
                }
            });
    }

    private fetchMessages(showLoading: boolean): void {
        const convId = this.conversationId();
        if (!convId) return;
        if (showLoading) this.loading.set(true);

        this.chatwootService
            .getMessages(convId)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    if (showLoading) this.loading.set(false);
                })
            )
            .subscribe({
                next: (resp) => {
                    if (resp?.success && resp?.data) {
                        this.mensajes.set(resp.data);
                        this.shouldScrollToBottom = true;
                        setTimeout(() => this.scrollToBottom(), 80);
                        this.advanceLastSeen(resp.data);
                    }
                }
            });
    }

    /** Carga TODAS las conversaciones del contacto (histórico completo) en el drawer. */
    private fetchAllMessagesByPhone(): void {
        const phone = this.phone;
        if (!phone) return;
        // Señal propia: openConversation maneja `loading` y pisa su valor en su finalize;
        // usar un flag separado evita que el empty ("Inicia la conversación") parpadee mientras
        // se trae el histórico completo (que ahora puede tardar al cargar el 100%).
        this.mensajesLoading.set(true);

        this.chatwootService
            .getAllMessagesByPhone(phone)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.mensajesLoading.set(false))
            )
            .subscribe({
                next: (resp) => {
                    if (resp?.success && resp?.data) {
                        this.mensajes.set(resp.data);
                        this.shouldScrollToBottom = true;
                        setTimeout(() => this.scrollToBottom(), 80);
                        this.advanceLastSeen(resp.data);
                    }
                }
            });
    }

    /**
     * Polling: mensajes vienen solo de la conv activa. Se reemplaza ese tramo del histórico
     * conservando los mensajes de las otras conversaciones (más viejas / cerradas) ya cargadas.
     */
    private mergeMessages(serverMessages: IChatwootMessage[]): void {
        const activeConvId = this.conversationId();
        if (!activeConvId) return;

        const current = this.mensajes();

        // conversation_created_at de la conv activa: los mensajes del polling (endpoint de UNA
        // conversación) NO lo traen; sin backfill quedan con ca=0 y el sort los manda ANTES
        // del histórico de conversaciones viejas (bug de orden visible).
        const caActiva =
            this.activeConvCreatedAt ??
            current.find((m) => m.conversation_id === activeConvId && m.conversation_created_at != null)
                ?.conversation_created_at ??
            undefined;

        // Etiquetar los mensajes recién traídos con la conv activa para mantener consistencia
        const taggedNew = serverMessages.map((m) => ({
            ...m,
            conversation_id: m.conversation_id ?? activeConvId,
            conversation_created_at: m.conversation_created_at ?? caActiva
        }));

        const optimisticIds = current.filter((m) => m.id < 0).map((m) => m.id);
        // Mensajes de OTRAS conversaciones (histórico antiguo) — se preservan
        const others = current.filter(
            (m) => m.conversation_id !== undefined && m.conversation_id !== activeConvId
        );
        // Mensajes optimistas (id negativo, en la conv activa) — mismo backfill de ca
        const optimistic = current
            .filter((m) => optimisticIds.includes(m.id))
            .map((m) => ({ ...m, conversation_created_at: m.conversation_created_at ?? caActiva }));

        // Nuevos ENTRANTES (no estaban en `current`): el agente tiene el chat abierto viendo
        // llegar el mensaje → queda visto. Se evalúa antes del set/merge, y solo se llama cuando
        // hay novedad real (no en cada poll sin cambios).
        const currentIds = new Set(current.map((m) => m.id));
        const hasNewIncoming = taggedNew.some((m) => m.message_type === 0 && !currentIds.has(m.id));

        const merged = [...others, ...taggedNew, ...optimistic].sort((a, b) => {
            const ca = a.conversation_created_at ?? 0;
            const cb = b.conversation_created_at ?? 0;
            if (ca !== cb) return ca - cb;
            // created_at viene en SEGUNDOS: mensajes del mismo segundo empatan → desempate
            // por id (creciente en Chatwoot; optimistas id<0 quedan antes de su copia real).
            if (a.created_at !== b.created_at) return a.created_at - b.created_at;
            return a.id - b.id;
        });

        const previousLength = current.length;
        this.mensajes.set(merged);
        this.advanceLastSeen(taggedNew);
        if (merged.length > previousLength) {
            this.shouldScrollToBottom = true;
            if (this.idCandidato) {
                this.notificationService.markRead(this.idCandidato);
            }
        }
        if (hasNewIncoming) {
            this.markSeenSilently(activeConvId);
        }
    }

    /**
     * Marca la conversación como vista ante Chatwoot (resetea unread_count). Fire-and-forget:
     * un fallo de seen no debe romper el chat, solo se registra en consola.
     */
    private markSeenSilently(conversationId: number): void {
        this.chatwootService
            .markSeen(conversationId)
            .pipe(
                takeUntil(this.destroy$),
                catchError((err) => {
                    // eslint-disable-next-line no-console
                    console.warn('[Chatwoot] markSeen falló (no crítico):', err);
                    return of(null);
                })
            )
            .subscribe();
    }

    /** Avanza lastSeenAt al timestamp del último incoming visto en el drawer. */
    private advanceLastSeen(messages: IChatwootMessage[]): void {
        const convId = this.conversationId();
        if (!convId || !messages?.length) return;
        const lastIncomingAt = messages
            .filter((m) => m.message_type === 0)
            .reduce((max, m) => (m.created_at > max ? m.created_at : max), 0);
        if (lastIncomingAt > 0) {
            this.notificationService.updateLastSeenForConversation(convId, lastIncomingAt);
        }
    }

    private reset(): void {
        this.mensajes.set([]);
        this.mensajesLoading.set(false);
        this.conversationId.set(null);
        this.canReply.set(true);
        this.ahora.set(Date.now());
        this.errorMessage.set(null);
        this.templates.set([]);
        this.templatesVisible.set(false);
        this.selectedTemplate.set(null);
        this.templateParams.set({});
        this.resetIaState();
        this.context.set(null);
        this.infoExpanded.set(false);
        this.nuevoMensaje = '';
    }

    toggleInfo(): void {
        this.infoExpanded.update((v) => !v);
    }

    fetchContext(): void {
        const convId = this.conversationId();
        if (!convId) return;
        this.contextLoading.set(true);
        this.chatwootService
            .getContext(convId)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.contextLoading.set(false))
            )
            .subscribe({
                next: (resp) => {
                    if (resp?.success && resp?.data) {
                        this.context.set(resp.data);
                    }
                }
            });

        if (this.availableLabels().length === 0) {
            this.chatwootService
                .getAvailableLabels()
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (resp) => {
                        if (resp?.success && resp?.data) {
                            this.availableLabels.set(
                                resp.data.map((l) => ({ title: l.title, color: l.color }))
                            );
                        }
                    }
                });
        }
    }

    setLabels(newLabels: string[]): void {
        const convId = this.conversationId();
        if (!convId) return;
        const previous = this.labels();
        // Optimistic update
        this.context.update((ctx) => (ctx ? { ...ctx, labels: newLabels } : ctx));
        this.updatingAttribute.set('labels');
        this.chatwootService
            .updateLabels(convId, newLabels)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.updatingAttribute.set(null))
            )
            .subscribe({
                next: (resp) => {
                    if (resp?.success && resp?.data) {
                        this.context.set(resp.data);
                    } else {
                        // Revert
                        this.context.update((ctx) => (ctx ? { ...ctx, labels: previous } : ctx));
                        this.toastr.error(resp?.message ?? 'No se pudieron actualizar etiquetas.', 'Chatwoot');
                    }
                },
                error: () => {
                    this.context.update((ctx) => (ctx ? { ...ctx, labels: previous } : ctx));
                    this.toastr.error('Error de red al actualizar etiquetas.', 'Chatwoot');
                }
            });
    }

    toggleBot(): void {
        const convId = this.conversationId();
        if (!convId) return;
        const current = this.botEnabled();
        const next = current === true ? 'OFF' : 'ON';
        this.updateConversationAttribute('estado_bot', next);
    }

    updateConversationAttribute(key: string, value: string): void {
        const convId = this.conversationId();
        const ctx = this.context();
        if (!convId || !ctx) return;

        const merged = { ...(ctx.conversationCustomAttributes ?? {}), [key]: value };
        this.updatingAttribute.set(key);
        this.chatwootService
            .updateConversationCustomAttributes(convId, merged)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.updatingAttribute.set(null))
            )
            .subscribe({
                next: (resp) => {
                    if (resp?.success && resp?.data) {
                        this.context.set(resp.data);
                        this.toastr.success(`${key} actualizado.`, 'Chatwoot');
                    } else {
                        this.toastr.error(resp?.message ?? 'No se pudo actualizar.', 'Chatwoot');
                    }
                },
                error: () => this.toastr.error('Error de red al actualizar.', 'Chatwoot')
            });
    }

    updateContactAttribute(key: string, value: string): void {
        const convId = this.conversationId();
        const ctx = this.context();
        const contactId = ctx?.contact?.id;
        if (!convId || !contactId) return;

        const merged = { ...(ctx?.contact?.custom_attributes ?? {}), [key]: value };
        this.updatingAttribute.set(`contact:${key}`);
        this.chatwootService
            .updateContactCustomAttributes(convId, contactId, merged)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.updatingAttribute.set(null))
            )
            .subscribe({
                next: (resp) => {
                    if (resp?.success && resp?.data) {
                        this.context.set(resp.data);
                        this.toastr.success(`${key} (contacto) actualizado.`, 'Chatwoot');
                    } else {
                        this.toastr.error(resp?.message ?? 'No se pudo actualizar.', 'Chatwoot');
                    }
                },
                error: () => this.toastr.error('Error de red al actualizar.', 'Chatwoot')
            });
    }

    removeLabel(label: string): void {
        const convId = this.conversationId();
        const current = this.labels();
        if (!convId) return;
        const updated = current.filter((l) => l !== label);

        this.updatingAttribute.set('labels');
        this.chatwootService
            .updateLabels(convId, updated)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => this.updatingAttribute.set(null))
            )
            .subscribe({
                next: (resp) => {
                    if (resp?.success && resp?.data) {
                        this.context.set(resp.data);
                    } else {
                        this.toastr.error(resp?.message ?? 'No se pudo quitar etiqueta.', 'Chatwoot');
                    }
                },
                error: () => this.toastr.error('Error de red al quitar etiqueta.', 'Chatwoot')
            });
    }

    formatAttrValue(value: unknown): string {
        if (value === null || value === undefined) return '—';
        if (typeof value === 'object') return JSON.stringify(value);
        return String(value);
    }

    private toAttributeEntries(record: Record<string, unknown> | null | undefined): Array<{ key: string; value: unknown }> {
        if (!record) return [];
        return Object.keys(record)
            .sort()
            .map((k) => ({ key: k, value: record[k] }));
    }

    private parseBoolean(value: unknown): boolean {
        if (typeof value === 'boolean') return value;
        if (typeof value === 'number') return value !== 0;
        if (typeof value === 'string') {
            const s = value.trim().toLowerCase();
            return s === 'on' || s === 'true' || s === 'si' || s === 'sí' || s === '1' || s === 'yes';
        }
        return false;
    }
}
